import {
    RootEntity as PackageRootEntity,
    QueueDefinition,
} from '@axle-health/work-queue-definitions';

import { DashboardContainer, RootEntity } from './types';

/**
 * The half of a container the definitions package does not carry.
 *
 * Row links, empty-table copy, ordering, bulk actions and default-enabled state are
 * presentation, and the reporting service that shares these definitions has no use
 * for them. Several are React-shaped: `rowToLink` returns a route and `extraProps`
 * names bulk actions.
 */
type Presentation<EntityT extends RootEntity, ExtraProps> = {
    defaultEnabled: boolean;
    hoverText?: string;
    queryOptions?: DashboardContainer<EntityT, ExtraProps>['queryOptions'];
    extraProps?: ExtraProps;
    tableConfig: Omit<DashboardContainer<EntityT, ExtraProps>['tableConfig'], 'entity' | 'filters'>;
};

/**
 * The instant and zone a container's filters resolve against.
 *
 * Evaluated once when this module loads, which is what the dashboard did before the
 * definitions moved into the package: the filters were object literals containing
 * `new Date()`, so they froze at page load in the browser's zone. Keeping that
 * exactly preserves behaviour, and the parity test asserts it.
 *
 * Resolving per render in the org's zone is the change worth making next, and worth
 * making on its own, because it changes which rows a queue returns near midnight.
 */
const moduleLoadContext = {
    now: new Date(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

/**
 * Builds a dashboard container from a published queue definition plus the
 * presentation that stays here.
 *
 * The definition supplies the key, label, entity, snoozable and heavyQuery flags and
 * the filters, so none of those are declared twice.
 */
export const fromDefinition = <EntityT extends RootEntity, ExtraProps = object>(
    definition: QueueDefinition,
    presentation: Presentation<EntityT, ExtraProps>,
): DashboardContainer<EntityT, ExtraProps> =>
    ({
        key: definition.key,
        label: definition.label,
        snoozable: definition.snoozable,
        ...(definition.heavyQuery ? { heavyQuery: true } : {}),
        ...(presentation.hoverText === undefined ? {} : { hoverText: presentation.hoverText }),
        ...(presentation.queryOptions === undefined
            ? {}
            : { queryOptions: presentation.queryOptions }),
        ...(presentation.extraProps === undefined ? {} : { extraProps: presentation.extraProps }),
        defaultEnabled: presentation.defaultEnabled,
        tableConfig: {
            ...presentation.tableConfig,
            // The package's RootEntity mirrors this one value for value; the cast
            // bridges the two nominal enums rather than converting anything.
            entity: definition.rootEntity as unknown as EntityT,
            filters: definition.buildFilters(moduleLoadContext),
        },
    } as DashboardContainer<EntityT, ExtraProps>);

/** Looks a queue up by key, failing loudly rather than rendering a queue-less dashboard. */
export const definitionFor = (queues: QueueDefinition[], key: string): QueueDefinition => {
    const found = queues.find((queue) => queue.key === key);
    if (!found) {
        throw new Error(`No published queue definition for ${key}`);
    }
    return found;
};

/** Re-exported so callers do not need to import from two places. */
export type { QueueDefinition, PackageRootEntity };
